<template>
  <section v-if="stacks.length" :class="$style.stacked">
    <header v-if="header && headerLevel">
      <component :is="headerLevel" v-html="header"></component>
    </header>
    <div
      v-for="(stack, index) in stacks"
      :key="index"
      :class="[$style.content, { [$style.noheader]: !(header && headerLevel) }]"
    >
      <figure :class="[$style.figure, { 'aspect-container summary-image-frame': !stack.link }]">
        <PreparedAnchor
          v-if="stack.link"
          :to="stack.link"
          :behavior="getTarget(stack.new_tab)"
          class="aspect-container summary-image-frame"
        >
          <picture>
            <img
              :alt="stack.altText"
              :src="stack.image"
              :srcset="stack.imageSrcset"
              class="aspect-image"
              loading="lazy"
              :sizes="imageSizes"
            />
          </picture>
        </PreparedAnchor>
        <picture v-else>
          <img
            :alt="stack.altText"
            :src="stack.image"
            :srcset="stack.imageSrcset"
            class="aspect-image"
            loading="lazy"
            :sizes="imageSizes"
          />
        </picture>
      </figure>
      <section :class="$style.section">
        <header v-if="stack.title || stack.subtitle" :class="$style.header">
          <h4 v-if="stack.title && stack.link">
            <PreparedAnchor :to="stack.link" :behavior="getTarget(stack.new_tab)">
              {{ stack.title }}
            </PreparedAnchor>
          </h4>
          <h4 v-else-if="stack.title">
            {{ stack.title }}
          </h4>
          <h5
            v-if="stack.subtitle"
            :class="{ [$style.notitle]: !stack.title }"
            v-html="stack.subtitle"
          ></h5>
        </header>
        <div
          v-if="stack.excerpt"
          :class="[
            $style.excerpt,
            { [$style.notitle]: !stack.title && !stack.subtitle },
            'space-8-10-above',
          ]"
          v-html="stack.excerpt"
        ></div>
        <div
          v-if="stack.button"
          :class="{ 'space-8-10-above': stack.button.button_type !== 'icon' }"
        >
          <a
            v-if="stack.button.pdf"
            :href="getLink(stack.button)"
            :class="getType(stack.button.button_type)"
            :target="
              getLink(stack.button).includes('http') ? getTarget(stack.button.new_tab) : '_blank'
            "
            v-html="stack.button.title"
          ></a>
          <PreparedAnchor
            v-else
            :to="getLink(stack.button)"
            :behavior="getTarget(stack.button.new_tab)"
            :class="getType(stack.button.button_type)"
          >
            {{ stack.button.title }}
          </PreparedAnchor>
        </div>
      </section>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue';

const PreparedAnchor = defineAsyncComponent(() => import('@/components/common/PreparedAnchor.vue'));

interface Button {
  button_type: string;
  link: string;
  new_tab: string | boolean;
  pdf: string;
  title: string;
}

const props = defineProps<{
  header?: string;
  headerLevel?: string;
  isSixSpan?: boolean;
  stacks: Array<{
    altText: string;
    button: Button;
    excerpt: string;
    imageSrcset: string;
    image: string;
    link: string;
    new_tab: string | boolean;
    subtitle: string;
    title: string;
  }>;
}>();

const imageSizes = computed(() => {
  return props.isSixSpan ? '(min-width: 960px) 620px, 100vw' : '(min-width: 960px) 470px, 100vw';
});

function getLink(button: Button): string {
  return button.pdf || button.link;
}

function getTarget(target: string | boolean): string {
  if (target && target === '1') {
    return '_blank';
  }

  return '_self';
}

function getType(type: string): string {
  switch (type) {
    case 'icon':
      return 'more-link';
    case 'primary':
      return 'button-primary';
    case 'secondary':
      return 'button-secondary';
    default:
      return '';
  }
}
</script>

<style lang="scss" module>
.content {
  display: block;
  margin-top: 3rem;

  &:first-of-type {
    margin-top: 1rem;

    &.noheader {
      margin-top: 0;
    }
  }

  a[href$='.pdf'][href$='.pdf'],
  section > a {
    display: inline-block;
    margin-bottom: 0;
    margin-right: 0;
    margin-top: 1rem;
  }
}

.excerpt {
  p:first-of-type {
    margin-top: 0;
  }

  &.notitle {
    margin-top: 1rem;
  }
}

.header {
  margin-top: 1rem;

  a {
    text-decoration: none;
  }

  h4 {
    margin-top: 0;
  }

  h5 {
    margin-top: 0;
  }
}

.notitle {
  margin-top: 0;

  p {
    margin-top: 0;
  }
}

.stacked {
  > header {
    h2,
    h3,
    h4 {
      margin: 0;
    }
  }
}

@media (min-width: $break-point) {
  .content {
    @include grid;
  }

  .excerpt {
    &.notitle {
      margin-top: 0;
    }
  }

  .figure {
    @include space-5-above;
  }

  .figure,
  .section {
    grid-column: span 6;
  }

  .header {
    margin-top: 0;
  }
}
</style>
